"use client";

import { useState } from "react";
import { SPORT_TAGS } from "./best-time-utils";
import { scoreOutOf100 } from "@/lib/conditions/score-display";

type FilterState = {
  minConditions: number;
  maxBusyness: number;
  maxLiftPass: number;
  minPisteKm: number;
  tags: string[];
};

type Props = {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  sport: string;
  hasPricing: boolean;
  maxPrice: number;
};

const BUSYNESS_STEPS = [
  { value: 0.25, label: "Quiet only" },
  { value: 0.5, label: "Up to moderate" },
  { value: 0.75, label: "Up to busy" },
  { value: 1.0, label: "Any" },
];

const PRICE_STEPS = [
  { value: 250, label: "Under €250/wk" },
  { value: 400, label: "Under €400/wk" },
  { value: 600, label: "Under €600/wk" },
];

const AREA_STEPS = [
  { value: 0, label: "Any size" },
  { value: 150, label: "Medium+" },
  { value: 300, label: "Large" },
];

export function FilterPanel({
  filters,
  onChange,
  sport,
  hasPricing,
  maxPrice,
}: Props) {
  const [open, setOpen] = useState(false);

  const sportTags = SPORT_TAGS[sport] ?? [];
  const isSkiSport = sport === "ski" || sport === "off-piste";

  const activeCount =
    (filters.minConditions > 0 ? 1 : 0) +
    (filters.maxBusyness < 1.0 ? 1 : 0) +
    (hasPricing && filters.maxLiftPass < maxPrice ? 1 : 0) +
    (isSkiSport && filters.minPisteKm > 0 ? 1 : 0) +
    filters.tags.length;

  const update = (partial: Partial<FilterState>) =>
    onChange({ ...filters, ...partial });

  const toggleTag = (tag: string) => {
    const next = filters.tags.includes(tag)
      ? filters.tags.filter((t) => t !== tag)
      : [...filters.tags, tag];
    update({ tags: next });
  };

  const resetAll = () =>
    onChange({
      minConditions: 0,
      maxBusyness: 1.0,
      maxLiftPass: maxPrice,
      minPisteKm: 0,
      tags: [],
    });

  return (
    <div className="border border-border-light rounded-[16px] mb-6">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full p-5 cursor-pointer"
      >
        <div className="flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-navy opacity-60">
            <path d="M2 4h12M4 8h8M6 12h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <span className="text-[14px] font-semibold text-navy">Filters</span>
          {activeCount > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gold text-white text-[11px] font-bold">
              {activeCount}
            </span>
          )}
        </div>
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          className={`text-text-muted transition-transform ${open ? "rotate-180" : ""}`}
        >
          <path d="M4 6l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {!open && (
        <p className="text-[13px] text-text-muted px-5 pb-4 -mt-1">
          Narrow by conditions, crowds, price, and resort type.
        </p>
      )}

      {open && (
        <div className="px-5 pb-5 space-y-5 border-t border-border-light pt-4">
          {/* Min conditions */}
          <div>
            <label className="block text-[12px] font-semibold text-text-muted uppercase tracking-wide mb-2">
              Min conditions score: {filters.minConditions > 0 ? `Min ${scoreOutOf100(filters.minConditions)}/100` : "Any"}
            </label>
            <input
              type="range"
              min={0}
              max={10}
              step={0.5}
              value={filters.minConditions}
              onChange={(e) => update({ minConditions: Number(e.target.value) })}
              className="w-full accent-gold"
            />
          </div>

          {/* Max busyness */}
          <div>
            <label className="block text-[12px] font-semibold text-text-muted uppercase tracking-wide mb-2">
              Max busyness: {BUSYNESS_STEPS.find((s) => s.value >= filters.maxBusyness)?.label ?? "Any"}
            </label>
            <div className="flex flex-wrap gap-2">
              {BUSYNESS_STEPS.map((step) => (
                <button
                  key={step.value}
                  onClick={() => update({ maxBusyness: step.value })}
                  className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors ${
                    filters.maxBusyness === step.value
                      ? "bg-navy text-white"
                      : "bg-warm-bg text-text-muted hover:bg-cream"
                  }`}
                >
                  {step.label}
                </button>
              ))}
            </div>
          </div>

          {/* Max lift pass price */}
          {hasPricing && (
            <div>
              <label className="block text-[12px] font-semibold text-text-muted uppercase tracking-wide mb-2">
                Lift pass budget
              </label>
              <div className="flex flex-wrap gap-2">
                {PRICE_STEPS.map((step) => (
                  <button
                    key={step.value}
                    onClick={() => update({ maxLiftPass: step.value })}
                    className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors ${
                      filters.maxLiftPass === step.value
                        ? "bg-navy text-white"
                        : "bg-warm-bg text-text-muted hover:bg-cream"
                    }`}
                  >
                    {step.label}
                  </button>
                ))}
                <button
                  onClick={() => update({ maxLiftPass: maxPrice })}
                  className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors ${
                    filters.maxLiftPass >= maxPrice
                      ? "bg-navy text-white"
                      : "bg-warm-bg text-text-muted hover:bg-cream"
                  }`}
                >
                  Any
                </button>
              </div>
            </div>
          )}

          {/* Min ski area */}
          {isSkiSport && (
            <div>
              <label className="block text-[12px] font-semibold text-text-muted uppercase tracking-wide mb-2">
                Ski area size
              </label>
              <div className="flex flex-wrap gap-2">
                {AREA_STEPS.map((step) => (
                  <button
                    key={step.value}
                    onClick={() => update({ minPisteKm: step.value })}
                    className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors ${
                      filters.minPisteKm === step.value
                        ? "bg-navy text-white"
                        : "bg-warm-bg text-text-muted hover:bg-cream"
                    }`}
                  >
                    {step.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {sportTags.length > 0 && (
            <div>
              <label className="block text-[12px] font-semibold text-text-muted uppercase tracking-wide mb-1">
                Resort type
              </label>
              <p className="text-[11px] text-text-muted/60 mb-2">
                Must match all selected
              </p>
              <div className="flex flex-wrap gap-2">
                {sportTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => toggleTag(tag)}
                    className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors ${
                      filters.tags.includes(tag)
                        ? "bg-navy text-white"
                        : "bg-warm-bg text-text-muted hover:bg-cream"
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Reset */}
          {activeCount > 0 && (
            <button
              onClick={resetAll}
              className="text-[12px] text-gold hover:text-gold-light transition-colors font-medium"
            >
              Reset all filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export type { FilterState };
